// Trovva brand colors and design tokens — vibrant, alive, energetic

const lightColors = {
  primary: '#2563EB',       // Bold blue — confident, trustworthy
  primaryLight: '#3B82F6',  // Lighter blue for hovers/highlights
  primaryDark: '#1E3A5F',   // Deep navy for sidebar
  accent: '#F43F5E',        // Vibrant rose-red — energy, urgency, action
  accentLight: '#FB7185',   // Soft rose for hover states
  bg: '#F9F7F4',            // Warm off-white — inviting, not clinical
  card: '#FFFFFF',
  text: '#1E293B',          // Rich dark slate
  textLight: '#64748B',     // Warm gray for secondary text
  success: '#16A34A',       // Bold green — unmistakable positive
  warning: '#F59E0B',       // Amber — warm and attention-grabbing
  danger: '#DC2626',        // Strong red — clear alerts
  info: '#0EA5E9',          // Sky blue — fresh, informational
  border: '#E8E4DF',        // Warm border to match warm bg
};

const darkColors = {
  primary: '#3B82F6',
  primaryLight: '#60A5FA',
  primaryDark: '#0F172A',
  accent: '#FB7185',
  accentLight: '#FDA4AF',
  bg: '#0F172A',
  card: '#1E293B',
  text: '#F1F5F9',
  textLight: '#94A3B8',
  success: '#22C55E',
  warning: '#FBBF24',
  danger: '#EF4444',
  info: '#38BDF8',
  border: '#334155',
};

const shared = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
  },
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
  },
  shadow: {
    sm: '0 1px 3px rgba(30,41,59,0.08)',
    md: '0 4px 12px rgba(30,41,59,0.10)',
    lg: '0 10px 30px rgba(30,41,59,0.14)',
  },
};

const darkShadow = {
  sm: '0 1px 3px rgba(0,0,0,0.2)',
  md: '0 4px 6px rgba(0,0,0,0.25)',
  lg: '0 10px 25px rgba(0,0,0,0.4)',
};

// ── Theme state ────────────────────────────────────────────────
// Mutable theme object so existing `import { theme }` keeps working.
// Call setDarkMode(true/false) to toggle at runtime.

export const theme = {
  colors: { ...lightColors },
  spacing: { ...shared.spacing },
  radius: { ...shared.radius },
  shadow: { ...shared.shadow },
};

let _isDark = false;
const _listeners = new Set();

/**
 * Get current dark mode state.
 */
export function isDarkMode() { return _isDark; }

/**
 * Toggle dark mode. Updates the theme object in-place and notifies listeners.
 */
export function setDarkMode(dark) {
  _isDark = dark;
  const colors = dark ? darkColors : lightColors;
  Object.assign(theme.colors, colors);
  Object.assign(theme.shadow, dark ? darkShadow : shared.shadow);

  // Persist preference
  try { localStorage.setItem('trovva_dark_mode', dark ? '1' : '0'); } catch {}

  // Update document for scrollbar / native elements
  if (typeof document !== 'undefined') {
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    document.body.style.background = theme.colors.bg;
  }

  // Notify React listeners
  _listeners.forEach(fn => fn(dark));
}

/**
 * Subscribe to dark mode changes. Returns unsubscribe function.
 */
export function onThemeChange(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/**
 * Initialize from saved preference or system setting.
 * Call once at app startup.
 */
export function initTheme() {
  try {
    const saved = localStorage.getItem('trovva_dark_mode');
    if (saved === '1') {
      setDarkMode(true);
      return;
    }
    if (saved === '0') return; // explicitly light
  } catch {}

  // System preference
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    setDarkMode(true);
  }
}

// Priority colors for maintenance, leads, etc.
export const priorityColors = {
  urgent: theme.colors.danger,
  high: theme.colors.danger,
  medium: theme.colors.warning,
  low: theme.colors.success,
};

// Status colors — bold and distinguishable
export const statusColors = {
  new: '#6366F1',           // Indigo
  contacted: '#F59E0B',     // Amber
  qualified: '#0EA5E9',     // Sky blue
  active_search: '#2563EB', // Bold blue
  offer_submitted: '#8B5CF6', // Violet
  offer_accepted: '#16A34A',  // Green
  'under contract': theme.colors.success,
  closed: theme.colors.success,
  lost: theme.colors.danger,
};

// Vendor preference colors
export const prefColors = {
  required: theme.colors.danger,
  preferred: theme.colors.info,
  approved: theme.colors.success,
  blocked: theme.colors.danger,
};

export const prefIcons = {
  required: '🔒',
  preferred: '⭐',
  approved: '✓',
  blocked: '⛔',
};
